// Qué le está pidiendo el paso 02 al cliente AHORA. Las tres pantallas se parecen demasiado —mismo
// icono redondo, mismo par de modalidades— y el vendedor no sabía cuál tenía enfrente.
import type { CustomerRoute } from './types';

/**
 * Lo que hay que capturarle al cliente en esta pantalla.
 *
 * - `FULL_KYC` — identificación por los dos lados + selfie.
 * - `FACEMATCH` — solo la selfie: el cliente ya tiene cuenta Pago Confiado.
 * - `PROOF_OF_ADDRESS` — un recibo, porque su documento no traía domicilio.
 */
export type Step02Request = 'FULL_KYC' | 'FACEMATCH' | 'PROOF_OF_ADDRESS';

export interface Step02RequestCopy {
    /** Chip del encabezado: qué se le pide al cliente, en tres palabras. */
    badge: string;
    badgeVariant: 'info' | 'secondary' | 'warning';
    /** Encabezado de la card del paso. */
    title: string;
    sub: string;
    /** Cuerpo de la pantalla donde se elige cómo capturar. */
    pickTitle: string;
    pickSub: string;
    tabletHint: string;
    phoneHint: string;
    /** Título del bloque del QR, que es el mismo componente en las tres. */
    qrTitle: string;
    /** Instrucción debajo del QR mientras el cliente no empieza. */
    scanTitle: string;
    scanSub: string;
    /** Qué hará el cliente con la tablet, antes de abrir el SDK. */
    handTabletSub: string;
    /** Encabezado del panel mientras el cliente no ha subido su información. */
    awaitingTitle: string;
    /** Encabezado desde INPUTS_COMPLETED. */
    validatingTitle: string;
    /** Título del modal del SDK en la tablet. */
    modalTitle: string;
    /** El silo aceptó la tablet pero no entregó con qué abrir el SDK. */
    configUnavailable: string;
}

const COPY: Record<Step02Request, Step02RequestCopy> = {
    FULL_KYC: {
        badge: 'Identificación y selfie',
        badgeVariant: 'info',
        title: 'Verificación de identidad',
        sub: 'Identificación y selfie. Al aprobar se abre su cuenta.',
        pickTitle: 'Entrega la tablet al cliente o muéstrale el QR',
        pickSub: 'Toma unos 45 segundos.',
        tabletHint: 'El cliente escanea su identificación aquí',
        phoneHint: 'El cliente la escanea desde su celular',
        qrTitle: 'Código QR de la verificación de identidad',
        scanTitle: 'Pide al cliente que escanee el código',
        scanSub: 'El resultado aparece aquí solo.',
        handTabletSub: 'Tendrá que fotografiar su INE y tomarse una selfie.',
        awaitingTitle: 'Esperando la identificación del cliente…',
        validatingTitle: 'Validando la identificación…',
        modalTitle: 'Verificación de identidad',
        configUnavailable:
            'No se pudo abrir la verificación en esta tablet. Usa el QR o avisa a soporte.',
    },
    FACEMATCH: {
        badge: 'Solo la selfie',
        badgeVariant: 'secondary',
        title: 'Confirmación de identidad por selfie',
        sub: 'Ya tiene cuenta: solo una selfie.',
        pickTitle: 'Entrega la tablet al cliente o muéstrale el QR',
        pickSub: 'El cliente solo se toma una selfie, sin identificación. Toma unos 15 segundos.',
        tabletHint: 'El cliente se toma la selfie aquí',
        phoneHint: 'El cliente se la toma desde su celular',
        qrTitle: 'Código QR de la selfie de confirmación',
        scanTitle: 'Pide al cliente que escanee el código',
        scanSub: 'El resultado aparece aquí solo.',
        handTabletSub: 'Tendrá que tomarse una selfie.',
        awaitingTitle: 'Esperando la selfie del cliente…',
        validatingTitle: 'Comparando la selfie…',
        modalTitle: 'Selfie de confirmación',
        configUnavailable:
            'No se pudo abrir la selfie en esta tablet. Usa el QR o avisa a soporte.',
    },
    PROOF_OF_ADDRESS: {
        badge: 'Recibo de domicilio',
        badgeVariant: 'warning',
        title: 'Comprobante de domicilio',
        sub: 'La identificación del cliente no trae domicilio. Con la foto de un recibo a su nombre se completa su expediente.',
        pickTitle: 'Falta el comprobante de domicilio del cliente',
        pickSub:
            'Sirve un recibo de luz, agua, predial o un estado de cuenta reciente, a nombre del cliente.',
        tabletHint: 'El cliente fotografía el recibo aquí',
        phoneHint: 'El cliente lo fotografía desde su celular',
        qrTitle: 'Código QR del comprobante de domicilio',
        scanTitle: 'Pide al cliente que escanee y fotografíe su comprobante',
        scanSub: 'Luz, agua, teléfono o predial, de los últimos 3 meses.',
        handTabletSub: 'Tendrá que fotografiar su comprobante de domicilio.',
        awaitingTitle: 'Esperando el comprobante del cliente…',
        validatingTitle: 'Revisando el comprobante…',
        modalTitle: 'Comprobante de domicilio',
        configUnavailable:
            'No se pudo preparar el comprobante en esa modalidad. Intenta con la otra o avisa a soporte.',
    },
};

/** El copy que le da identidad a la pantalla. */
export function step02RequestCopy(request: Step02Request): Step02RequestCopy {
    return COPY[request];
}

/**
 * Cuál de las tres se está pidiendo.
 *
 * El comprobante manda sobre la ruta del KYC: cuando toca, es la pantalla que el vendedor tiene
 * enfrente, y anunciar la identificación ahí lo hace pedirle al cliente algo que nadie va a pedirle.
 */
export function step02Request(
    route: CustomerRoute | undefined,
    askingForProof: boolean,
): Step02Request {
    if (askingForProof) return 'PROOF_OF_ADDRESS';
    // Ruta sin resolver ⇒ pantalla completa: anunciar de más es preferible a que el cliente llegue
    // sin su identificación.
    return route === 'FACEMATCH' ? 'FACEMATCH' : 'FULL_KYC';
}
